/**
 * MosaicComplianceCard.js — v6.32.163
 *
 * District-facing 1-page coverage snapshot. Pulls from
 * GET /api/mosaic/compliance/snapshot and renders:
 *   - Header chip with total screened + total intake rows
 *   - 6-segment level distribution bar (WIDA-parity color system)
 *   - Newcomer / SIFE / IEP / Former-ELL flag tiles
 *   - Grade and Top Languages breakdown
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import MosaicBucketBadge from './MosaicBucketBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MosaicComplianceCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/mosaic/compliance/snapshot`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load snapshot');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="border-2 border-gray-900 bg-white p-5 flex items-center gap-3 text-gray-500" data-testid="mosaic-compliance-card">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading MOSAIC coverage snapshot…
      </div>
    );
  }
  if (error) {
    return (
      <div className="border-2 border-rose-300 bg-rose-50 p-5 text-sm text-rose-800" data-testid="mosaic-compliance-card">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const total = data.total_screened || 0;
  const levels = data.by_level || [];
  const flags = data.flag_counts || {};

  return (
    <div className="border-2 border-gray-900 bg-white" data-testid="mosaic-compliance-card">
      <header className="flex items-center justify-between px-5 py-3 bg-gray-900 text-white">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-gray-300">
            MOSAIC Coverage Snapshot
          </div>
          <div className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {total} screened · {data.total_intake_rows} intake rows
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          className="text-gray-300 hover:text-white"
          data-testid="compliance-refresh"
          aria-label="Refresh compliance snapshot"
        >
          <RefreshCcw className="w-4 h-4" />
        </Button>
      </header>

      <div className="p-5 space-y-5">
        {/* Level distribution bar */}
        <section>
          <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
            Distribution by MOSAIC level
          </div>
          <div className="flex h-6 border border-gray-300" data-testid="compliance-distribution-bar">
            {levels.map((L) => {
              const pct = total > 0 ? (L.count / total) * 100 : 0;
              const styles = {
                1: 'bg-rose-300',
                2: 'bg-orange-300',
                3: 'bg-amber-300',
                4: 'bg-emerald-300',
                5: 'bg-blue-300',
                6: 'bg-indigo-300',
              };
              if (pct === 0) return null;
              return (
                <div
                  key={L.level}
                  style={{ width: `${pct}%` }}
                  className={`${styles[L.level]} text-[10px] font-bold flex items-center justify-center text-gray-900`}
                  title={`${L.label}: ${L.count}`}
                >
                  {pct >= 8 ? `${Math.round(pct)}%` : ''}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {levels.map((L) => (
              <div key={L.level} className="flex items-center gap-1.5 text-xs">
                <MosaicBucketBadge level={L.level} label={L.label} compact />
                <span className="tabular-nums text-gray-700">{L.count}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Flag tiles */}
        <section>
          <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
            Flag Counts
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'newcomer', label: 'Newcomer' },
              { key: 'sife', label: 'SIFE' },
              { key: 'iep_504', label: 'IEP / 504' },
              { key: 'former_ell', label: 'Former ELL' },
            ].map((f) => (
              <div
                key={f.key}
                className="border border-gray-300 p-3"
                data-testid={`compliance-flag-${f.key}`}
              >
                <div className="text-[11px] uppercase text-gray-500 tracking-wide">{f.label}</div>
                <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {flags[f.key] || 0}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Grade + Languages */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
              By Grade
            </div>
            <ul className="text-sm space-y-0.5">
              {(data.by_grade || []).slice(0, 8).map((g) => (
                <li key={g.grade} className="flex justify-between border-b border-gray-100 py-1">
                  <span className="font-semibold">Grade {g.grade}</span>
                  <span className="tabular-nums text-gray-600">{g.count}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
              Top Home Languages
            </div>
            <ul className="text-sm space-y-0.5">
              {(data.top_languages || []).slice(0, 8).map((l) => (
                <li key={l.language} className="flex justify-between border-b border-gray-100 py-1">
                  <span className="font-semibold">{l.language}</span>
                  <span className="tabular-nums text-gray-600">{l.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MosaicComplianceCard;
